import React from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { InfoIcon } from '@sainsburys-tech/bolt-icons'
import * as Styled from './MC003.styles'
import { getWrapperProps } from '../../../helpers/ComponentHelper'
import { createGolDataAttributes } from '../../../helpers/tracking'
import { colorToLunaToken } from '../../../helpers/theming/utilities'

/**
 * MC003 is a skyline banner
 */
const MC003 = ({
  heading,
  subheading,
  ctaCopy,
  linkTarget,
  linkTitle,
  linkUrl,
  onLinkClick,
  backgroundColor,
  textColor,
  theme,
  icon
}) => {
  const elementClassName = 'xs-row'
  const isSainsburys = theme.channel === 'sainsburys'
  const hiddenLink = isSainsburys ? undefined : 'xs'
  const columnProps = {
    md: 10,
    xs: 12
  }
  const wrapperProps = {
    className: elementClassName
  }
  const Wrapper = linkUrl ? Styled.WrapperAnchor : Styled.WrapperDiv

  const getGOLDataAttributes = (action) =>
    createGolDataAttributes('isolated', 'MC003 - NA', linkTitle, linkUrl, heading, action)

  const addGolTracking = (evt) => {
    const dataAttributes = getGOLDataAttributes()
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, url) => {
    if (onLinkClick) {
      onLinkClick(event, url)
    }
    addGolTracking(event)
  }

  return (
    <Wrapper
      data-test='mc003-wrapper'
      gutterValue={0}
      target={linkTarget}
      {...getWrapperProps(linkTitle, linkUrl, ctaClick, wrapperProps)}
      {...getGOLDataAttributes('Load')}
      backgroundColour={backgroundColor}
    >
      <Styled.Headings
        data-test='mc003-text-container'
        {...columnProps}
        hasIcon={!!icon}
        textColor={textColor}
        ctaExists={!!ctaCopy}
      >
        <Styled.HeadingWrapper>
          {icon === 'info' && (
            <InfoIcon
              channel={theme.channel}
              data-test='mc003-info-icon'
              height='1.5rem'
              width='1.5rem'
              fill={colorToLunaToken(textColor, theme) || theme.color.monochrome.darker}
            />
          )}
          <h4 data-test='mc003-heading'>{heading}</h4>
        </Styled.HeadingWrapper>
        {subheading && <p data-test='mc003-subeading'>{subheading}</p>}
      </Styled.Headings>
      {ctaCopy && (
        <Styled.Cta
          data-test='mc003-cta-container'
          block='md'
          md={2}
          xs={12}
          textColor={textColor}
          hidden={hiddenLink}
          hasIcon={!!icon}
        >
          <Styled.CmsCta data-test='mc003-cta' hidden={hiddenLink}>
            {ctaCopy}
          </Styled.CmsCta>
        </Styled.Cta>
      )}
    </Wrapper>
  )
}

MC003.propTypes = {
  /** Background color, defaults to grey-light */
  backgroundColor: PropTypes.string.isRequired,
  /** Text color, defaults to grey-charcoal */
  textColor: PropTypes.string.isRequired,
  /** Optional text for the CTA */
  ctaCopy: PropTypes.string,
  /** Required text for heading */
  heading: PropTypes.string.isRequired,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional link title, can be seen when hovering over the CTA */
  linkTitle: PropTypes.string,
  /** Optional link url */
  linkUrl: PropTypes.string,
  /** Optional text for subheading */
  subheading: PropTypes.string,
  /** Optional icon type */
  icon: PropTypes.string
}

MC003.defaultProps = {
  ctaCopy: '',
  linkTarget: '',
  linkTitle: '',
  linkUrl: '',
  subheading: '',
  icon: ''
}

export default withTheme(MC003)
